import { FC } from "react";
import { useSelector } from "react-redux";
import { Avatar, Box, Typography } from "@mui/material";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import theme from "@/theme";
import { RootState } from "@/store/store";
import { formatTimeAgo } from "@/helpers/getHoursDifference";
import { IConversation } from "@/store/api/conversationsApiData";
import { formatLikesCount } from "@/helpers/formatCount";
import EngagementBadges from "./EngagementBadges";

interface ConversationItemProps {
  conversation: IConversation;
  isSelected?: boolean;
  isUnread?: boolean;
  onOpen: (conversation: IConversation) => void;
  onReply: (conversation: IConversation) => void;
  onGoToPost: (conversation: IConversation) => void;
}

const renderContent = (content: string) => {
  const parts = content?.split(/(@\w+)/g);
  return parts?.map((part, index) =>
    part.startsWith("@") ? (
      <Box key={index} component="span" sx={{ color: theme.palette.custom.mainBlue }}>
        {part}
      </Box>
    ) : (
      <Box key={index} component="span">
        {part}
      </Box>
    )
  );
};

const actionTextStyle = (color: string) => ({
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "16px",
  color,
});

const ConversationItem: FC<ConversationItemProps> = ({
  conversation,
  isSelected,
  isUnread,
  onOpen,
  onReply,
  onGoToPost,
}) => {
  const isMention = conversation.type === "mention";
  const { author, post } = conversation;
  const currentUserUid = useSelector((state: RootState) => state.auth.user?.uid);
  const isOwnPost = post.author.uid === currentUserUid;

  return (
    <Box
      onClick={() => onOpen(conversation)}
      sx={{
        position: "relative",
        display: "flex",
        gap: "12px",
        alignItems: "flex-start",
        padding: "14px 24px 15px 32px",
        borderBottom: "1px solid #e4e6eb",
        cursor: "pointer",
        backgroundColor: isSelected
          ? "#eef4ff"
          : isUnread
            ? "#e9f2ff"
            : theme.palette.custom.mainWhite,
        transition: "background-color 0.8s ease",
        "&:hover": { backgroundColor: isSelected || isUnread ? "#eef4ff" : "#f7f9fc" },
      }}
    >
      {isUnread && (
        <Box
          sx={{
            position: "absolute",
            left: "14px",
            top: "22px",
            width: "7px",
            height: "7px",
            borderRadius: "50%",
            backgroundColor: theme.palette.custom.mainBlue,
          }}
        />
      )}

      <Avatar src={author.image?.url || ""} sx={{ width: 38, height: 38 }} />

      <Box
        sx={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                <Typography
                  sx={{
                    fontFamily: "SF Pro Text, Inter, sans-serif",
                    fontWeight: 700,
                    fontSize: "12px",
                    letterSpacing: "-0.24px",
                    color: "#3c3a3a",
                    whiteSpace: "nowrap",
                  }}
                >
                  {author.username}
                </Typography>
                {author.isPremium && (
                  <VerifiedRoundedIcon sx={{ fontSize: 14, color: "#1d9bf0" }} />
                )}
              </Box>
              <EngagementBadges
                like={conversation.like}
                zap={conversation.zap}
                purchase={conversation.purchase}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: "8px",
                  py: "2px",
                  borderRadius: "20px",
                  backgroundColor: isMention ? "#dbeafe" : "#dbfee7",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 600,
                    fontSize: "10px",
                    lineHeight: "15px",
                    color: isMention ? "#1a56db" : "#008001",
                  }}
                >
                  {isMention ? "@mention" : "reply"}
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 500,
                fontSize: "11px",
                lineHeight: "16px",
                color: "#9da6b0",
                whiteSpace: "nowrap",
              }}
            >
              {formatTimeAgo(conversation.createdAt)}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px", width: "100%" }}>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 400,
                  fontSize: "11px",
                  lineHeight: "16px",
                  color: "#9aa0b4",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {isOwnPost ? "Your post" : `Post by ${post.author.username}`}
              </Typography>
              <Box
                sx={{
                  width: "4px",
                  height: "4px",
                  borderRadius: "50%",
                  backgroundColor: "#9aa0b4",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 400,
                  fontSize: "11px",
                  lineHeight: "16px",
                  color: "#9aa0b4",
                  flex: "1 1 0",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {post.contentPreview}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 500,
                fontSize: "12px",
                lineHeight: "16px",
                color: "#5a5f72",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {renderContent(conversation.content)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "16px", pt: "6px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {conversation.isLiked ? (
              <FavoriteRoundedIcon sx={{ fontSize: 16, color: theme.palette.custom.mainBlue }} />
            ) : (
              <FavoriteBorderRoundedIcon
                sx={{ fontSize: 16, color: theme.palette.custom.mainBlue }}
              />
            )}
            <Typography sx={actionTextStyle(theme.palette.custom.mainBlue)}>
              {formatLikesCount(conversation.reactionsCount)}
            </Typography>
          </Box>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onReply(conversation);
            }}
            sx={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}
          >
            <ReplyRoundedIcon sx={{ fontSize: 16, color: theme.palette.custom.mainBlue }} />
            <Typography sx={actionTextStyle(theme.palette.custom.mainBlue)}>Reply</Typography>
          </Box>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onGoToPost(conversation);
            }}
            sx={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}
          >
            <OpenInNewRoundedIcon sx={{ fontSize: 15, color: "#9aa0b4" }} />
            <Typography sx={actionTextStyle("#9aa0b4")}>Go to post</Typography>
          </Box>
          <MoreHorizRoundedIcon sx={{ fontSize: 16, color: "#9aa0b4" }} />
        </Box>

        {conversation.repliesCount > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", pt: "4px" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {(conversation.recentRepliers ?? []).slice(0, 3).map((replier, index) => (
                <Avatar
                  key={replier.uid}
                  src={replier.image?.url || ""}
                  sx={{
                    width: "20px",
                    height: "20px",
                    border: "2px solid #fff",
                    marginLeft: index === 0 ? 0 : "-6px",
                  }}
                />
              ))}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "18px",
                  color: theme.palette.custom.mainBlue,
                }}
              >
                {conversation.repliesCount} replies in thread
              </Typography>
              <ChevronRightRoundedIcon
                sx={{ fontSize: 18, color: theme.palette.custom.mainBlue }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ConversationItem;
